import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useLocation, useNavigate } from 'react-router';
import SearchBox from '../../components/SearchBox/SearchBox';
import MainSlider from '../../components/MainSlider/MainSlider';
import CategorySlider from '../../components/CategorySlider/CategorySlider';
import ProductCard from '../../components/ProductCard/ProductCard';
import { useAppContext } from '../../contexts/AppContext';
import {
  fetchHomeDashboard,
  fetchProductsByCategory,
} from '../../services/homeDashboardService';

function mergeCategoryProductsByProduct(items) {
  const groupedProducts = new Map();

  items.forEach((item) => {
    const productKey = item.product_id || item.id || `${item.name}-${item.image}`;
    const existingItem = groupedProducts.get(productKey);
    const variantLabel = item.size || item.variant_name || item.weight || '';

    if (!existingItem) {
      groupedProducts.set(productKey, {
        ...item,
        id: item.product_id || item.id,
        size: item.size || item.variant_name || '',
        variants: variantLabel ? [variantLabel] : [],
      });
      return;
    }

    const existingPrice = Number(existingItem.price || 0);
    const nextPrice = Number(item.price || 0);

    if (nextPrice > 0 && (existingPrice === 0 || nextPrice < existingPrice)) {
      existingItem.price = item.price;
    }

    if (!existingItem.size && (item.size || item.variant_name)) {
      existingItem.size = item.size || item.variant_name;
    }

    if (variantLabel && !existingItem.variants.includes(variantLabel)) {
      existingItem.variants.push(variantLabel);
    }
  });

  return Array.from(groupedProducts.values());
}

function CategoryProductsSkeleton() {
  return (
    <>
      <Typography
        variant='subtitle1'
        noWrap
        sx={{ fontWeight: '700', fontSize: '1.15rem', mb: 1, mx: 2, mt: 2.5 }}
      >
        <Skeleton
          variant='text'
          width={190}
          height={34}
          sx={{ transform: 'none', borderRadius: 1.5 }}
        />
      </Typography>
      <Box className='product-grid'>
        {Array.from({ length: 4 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              borderRadius: 6,
              background: '#fff',
              boxShadow: '0 14px 26px rgba(17, 24, 39, 0.05)',
              overflow: 'hidden',
            }}
          >
            <Skeleton variant='rectangular' height={160} sx={{ borderRadius: 0 }} />
            <Box sx={{ px: 2, py: 1.75 }}>
              <Skeleton variant='text' width='72%' height={28} />
              <Skeleton variant='text' width='48%' height={22} sx={{ mt: 0.25 }} />
              <Skeleton variant='text' width='84%' height={22} sx={{ mt: 0.25 }} />
            </Box>
            <Box
              sx={{
                px: 2,
                pb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Skeleton variant='text' width={86} height={30} />
              <Skeleton variant='rounded' width={36} height={36} />
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
}

function CategoryEmptyState({ categoryName }) {
  return (
    <Box sx={{ px: 2, mt: 2.5 }}>
      <Box
        sx={{
          borderRadius: '30px',
          px: { xs: 2.2, sm: 3 },
          py: { xs: 3, sm: 3.5 },
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,246,241,0.96) 100%)',
          boxShadow: '0 18px 34px rgba(17, 24, 39, 0.07)',
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -24,
            right: -18,
            width: 120,
            height: 120,
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(255,141,120,0.22) 0%, rgba(255,141,120,0) 74%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -38,
            left: -12,
            width: 140,
            height: 140,
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(255,214,153,0.28) 0%, rgba(255,214,153,0) 72%)',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            width: 94,
            height: 94,
            borderRadius: '28px',
            mx: 'auto',
            mb: 2,
            background:
              'linear-gradient(180deg, rgba(255,241,224,0.98) 0%, rgba(255,224,194,0.95) 100%)',
            boxShadow: '0 18px 28px rgba(246, 166, 88, 0.24)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Inventory2OutlinedIcon sx={{ fontSize: 36, color: '#f27a57' }} />
          <Box
            sx={{
              position: 'absolute',
              right: -8,
              bottom: -8,
              width: 38,
              height: 38,
              borderRadius: '999px',
              background: 'linear-gradient(180deg, #ff8d78 0%, #ff5c67 100%)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 22px rgba(255, 92, 103, 0.22)',
            }}
          >
            <SearchOffRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
        </Box>

        <Typography
          sx={{
            position: 'relative',
            zIndex: 1,
            fontWeight: 800,
            fontSize: { xs: '1.15rem', sm: '1.35rem' },
            color: '#202733',
            mb: 0.75,
          }}
        >
          No products in {categoryName}
        </Typography>
        <Typography
          sx={{
            position: 'relative',
            zIndex: 1,
            maxWidth: 480,
            mx: 'auto',
            color: '#7f8793',
            lineHeight: 1.75,
          }}
        >
          We couldn&apos;t find any items in this category right now. Try another
          category or check back soon for new arrivals.
        </Typography>
      </Box>
    </Box>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppContext();
  const [dashboardData, setDashboardData] = useState({
    banners: [],
    categories: [],
    bestSelling: [],
    newArrivals: [],
    recommended: [],
    trending: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState(null);

  const userId = useMemo(
    () => user?.user_id ?? user?.id ?? user?.customer_id ?? '',
    [user]
  );

  useEffect(() => {
    let isMounted = true;

    const loadHomeDashboard = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await fetchHomeDashboard({ userId });

        if (!isMounted) {
          return;
        }

        setDashboardData({
          banners: response.banners,
          categories: response.categories,
          bestSelling: response.bestSelling,
          newArrivals: response.newArrivals,
          recommended: response.recommended,
          trending: response.trending,
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setLoadError(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadHomeDashboard();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    const preselectedCategory = location.state?.selectedCategory;

    if (preselectedCategory?.category_id) {
      handleCategoryClick(preselectedCategory);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const handleCategoryClick = async (category) => {
    setSelectedCategory(category);
    setIsCategoryLoading(true);
    setCategoryError(null);

    try {
      const response = await fetchProductsByCategory({
        categoryId: category.category_id,
        userId,
        page: 1,
      });
      setCategoryProducts(mergeCategoryProductsByProduct(response.items));
    } catch (error) {
      setCategoryError(error);
      setCategoryProducts([]);
    } finally {
      setIsCategoryLoading(false);
    }
  };

  const productSections = [
    {
      key: 'best-selling',
      title: 'Best selling',
      items: mergeCategoryProductsByProduct(dashboardData.bestSelling),
    },
    {
      key: 'new-arrivals',
      title: 'New arrivals',
      items: mergeCategoryProductsByProduct(dashboardData.newArrivals),
      layout: 'scroll',
    },
    {
      key: 'recommended',
      title: 'Recommended',
      items: mergeCategoryProductsByProduct(dashboardData.recommended),
      layout: 'scroll',
    },
    { key: 'trending', title: 'Trending', items: dashboardData.trending },
  ].filter((section) => section.items.length > 0);

  return (
    <div>
      <SearchBox />
      {loadError ? (
        <Alert severity='error' sx={{ mx: 2, mt: 2 }}>
          {loadError.message}
        </Alert>
      ) : null}

      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              px: 2,
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#202733' }}>
              #SpecialForYou
            </Typography>
            <Typography
              onClick={() => navigate('/offers')}
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                color: '#ff6d6d',
                cursor: 'pointer',
              }}
            >
              See All
            </Typography>
          </Box>
          <MainSlider banners={dashboardData.banners} />
          <CategorySlider
            title={'Categories'}
            items={dashboardData.categories}
            onCategoryClick={handleCategoryClick}
            selectedCategoryId={selectedCategory?.category_id}
            onSeeAll={() => navigate('/categories')}
          />

          {categoryError ? (
            <Alert severity='error' sx={{ mx: 2, mt: 1 }}>
              {categoryError.message}
            </Alert>
          ) : null}

          {isCategoryLoading ? <CategoryProductsSkeleton /> : null}

          {!isCategoryLoading && selectedCategory ? (
            categoryProducts.length ? (
              <ProductCard
                products={categoryProducts}
                title={`${selectedCategory.name} products`}
              />
            ) : (
              <CategoryEmptyState categoryName={selectedCategory.name} />
            )
          ) : null}

          {productSections.map((section) => (
            <ProductCard
              key={section.key}
              products={section.items}
              title={section.title}
              layout={section.layout}
            />
          ))}

          {!dashboardData.categories.length && !productSections.length ? (
            <Typography sx={{ px: 2, py: 4, color: 'text.secondary' }}>
              No storefront data is available right now. Please check back shortly.
            </Typography>
          ) : null}
        </>
      )}
    </div>
  );
}
